import React from "react";
import {
    Pane,
    Paragraph,
    Spinner,
    Text,
    TickCircleIcon,
    WarningSignIcon,
} from "evergreen-ui";
import { ContentPosition, DetailsPageSection } from "../../common/DetailsPageSection";
import { FooterActionBar } from "../../common/FooterActionBar";
import { ProductAutocompleteMultiSelector } from "../../common/ProductAutocompleteMultiSelector";
import { ColorPalette } from "../../context/Theme";
import { FooterStatus, useShippingTimelineResyncStateManager } from "./ShippingTimelineResyncStateManager";

const DESCRIPTION =
    "If a shipping timeline metafield has changed, use this page to recompute the shipping " +
    "timelines on all order line items that are still pending fulfillment. You can filter by " +
    "product (which will only recompute order line items for that product's variants) rather " +
    "than all unfulfilled order line items.";

const FOOTER_INTENT_COLOR: Record<FooterStatus["intent"], string> = {
    none: ColorPalette.lightGreyText,
    pending: ColorPalette.primaryText,
    danger: ColorPalette.statusBad,
    success: ColorPalette.statusGood,
};

const FooterStatusMessage: React.FC<{ status: FooterStatus }> = ({ status }) => (
    // No fixed max width: the message fills the space up to the action buttons
    // and only wraps once they crowd it (the footer flex row shrinks this side).
    <Pane display={"flex"} alignItems={"center"} minWidth={0}>
        {status.intent === "pending" && <Spinner size={16} marginRight={8} flexShrink={0} />}
        {status.intent === "success" && (
            <TickCircleIcon color={ColorPalette.statusGood} marginRight={8} flexShrink={0} />
        )}
        {status.intent === "danger" && (
            <WarningSignIcon color={ColorPalette.statusBad} marginRight={8} flexShrink={0} />
        )}
        <Text fontSize={12} color={FOOTER_INTENT_COLOR[status.intent]}>
            {status.message}
        </Text>
    </Pane>
);

export const ShippingTimelineResyncTab = (): JSX.Element => {
    const {
        searchProducts,
        selectedProducts,
        onAddProduct,
        onRemoveProduct,
        onClearSelection,
        canSubmit,
        resyncLoading,
        onSubmit,
        footerStatus,
    } = useShippingTimelineResyncStateManager();

    return (
        <Pane>
            <DetailsPageSection
                hideBorder={true}
                contentPosition={ContentPosition.left}
                heading={
                    <Pane display={"flex"} flexDirection={"row"} alignItems={"baseline"}>
                        <h3 style={{ margin: 0 }}>Filter by product</h3>
                        <Text fontSize={12} marginLeft={10} color={ColorPalette.lightGreyText}>
                            Optional
                        </Text>
                    </Pane>
                }
                description={
                    <Paragraph fontSize={12} color={"muted"} marginTop={8} marginBottom={20} maxWidth={620}>
                        {DESCRIPTION}
                    </Paragraph>
                }
            >
                <ProductAutocompleteMultiSelector
                    searchProducts={searchProducts}
                    selectedProducts={selectedProducts}
                    onAddProduct={onAddProduct}
                    onRemoveProduct={onRemoveProduct}
                />
            </DetailsPageSection>

            <FooterActionBar
                primaryButtonText={"Update Timelines"}
                isPrimaryLoading={resyncLoading}
                primaryButtonDisabled={!canSubmit}
                onPrimaryClick={onSubmit}
                showSecondaryButton={false}
                // "Clear selection" only appears while products are selected; the
                // FooterActionBar hides the tertiary button when text is undefined.
                tertiaryButtonText={selectedProducts.length > 0 ? "Clear selection" : undefined}
                onTertiaryClick={onClearSelection}
                infoComponent={<FooterStatusMessage status={footerStatus} />}
            />
        </Pane>
    );
};
